// src/services/demandSuggestionService.js

import { apiRequest } from "./apiService";
import { aiApiRequest } from "./aiApiService";

const encId = (id) => encodeURIComponent(String(id == null ? "" : id));

const AI_SUGGESTION_TIMEOUT_MS = 30000;
const SHORT_WRITE_TIMEOUT_MS = 10000;

/** Normalize plain text fields. */
const normalizeText = (value) => String(value == null ? "" : value).trim();

/** Normalize optional numeric radius. Defaults to 5 miles. */
const normalizeRadiusMiles = (value, fallback = 5) => {
  const n = Number(value);

  if (!Number.isFinite(n) || n <= 0) {
    return fallback;
  }

  return Math.round(n);
};

const requireObjectPayload = (payload, label) => {
  if (!payload || typeof payload !== "object" || Array.isArray(payload)) {
    throw new Error(`Valid payload is required for ${label}.`);
  }
};

const requireSuggestionId = (suggestionId) => {
  if (suggestionId == null || String(suggestionId).trim() === "") {
    throw new Error("suggestionId is required.");
  }
};

const buildDemandSuggestionBody = (payload) => {
  requireObjectPayload(payload, "campaign demand suggestions");

  const location = payload.location || {};

  const businessName = normalizeText(payload.businessName);
  const businessType = normalizeText(payload.businessType);
  const postcode = normalizeText(location.postcode ?? payload.postcode);
  const city = normalizeText(location.city ?? payload.city);

  const radiusMiles = normalizeRadiusMiles(
    location.radiusMiles ?? payload.radiusMiles,
    5
  );

  const goal = normalizeText(payload.goal);
  const productOrService = normalizeText(payload.productOrService);
  const targetCustomer =
    normalizeText(payload.targetCustomer) || "local customers";

  if (!businessType) {
    throw new Error("businessType is required.");
  }

  if (!city && !postcode) {
    throw new Error("city or postcode is required.");
  }

  if (!goal) {
    throw new Error("goal is required.");
  }

  if (!productOrService) {
    throw new Error("productOrService is required.");
  }

  return {
    ...(businessName ? { businessName } : {}),
    businessType,
    location: {
      ...(postcode ? { postcode } : {}),
      ...(city ? { city } : {}),
      radiusMiles,
    },
    goal,
    productOrService,
    targetCustomer,
  };
};

/**
 * POST /campaigns/demand-suggestions
 * Body: CampaignDemandSuggestionRequest
 *
 * Generates campaign demand suggestions.
 * This may call AI, so it uses the AI timeout profile.
 */
export const generateCampaignDemandSuggestions = (payload) => {
  const body = buildDemandSuggestionBody(payload);

  return aiApiRequest("/campaigns/demand-suggestions", "POST", body, {
    timeoutMs: AI_SUGGESTION_TIMEOUT_MS,
  });
};

/**
 * POST /campaigns/demand-suggestions/{suggestionId}/select
 *
 * Marks a generated demand suggestion as selected.
 * Optional body can include campaignId after campaign creation.
 */
export const selectCampaignDemandSuggestion = (suggestionId, payload = {}) => {
  requireSuggestionId(suggestionId);

  const body =
    payload && typeof payload === "object" && !Array.isArray(payload)
      ? {
          ...(payload.campaignId != null &&
          String(payload.campaignId).trim() !== ""
            ? { campaignId: normalizeText(payload.campaignId) }
            : {}),
        }
      : {};

  return apiRequest(
    `/campaigns/demand-suggestions/${encId(suggestionId)}/select`,
    "POST",
    body,
    {
      timeoutMs: SHORT_WRITE_TIMEOUT_MS,
    }
  );
};

/**
 * POST /campaigns/demand-suggestions/{suggestionId}/dismiss
 *
 * Marks a generated demand suggestion as dismissed.
 */
export const dismissCampaignDemandSuggestion = (suggestionId) => {
  requireSuggestionId(suggestionId);

  return apiRequest(
    `/campaigns/demand-suggestions/${encId(suggestionId)}/dismiss`,
    "POST",
    null,
    {
      timeoutMs: SHORT_WRITE_TIMEOUT_MS,
    }
  );
};